import { useEffect, useMemo, useState } from "react";
import { X, Calendar, AlertTriangle, ListChecks } from "lucide-react";
import { formatMoney } from "./pipelineHelpers";

// =====================================================================
// SelectVisitProceduresDialog — pick which Tx-Plan procedures go into
// the next appointment.
//
// Why this exists
// ---------------
// TxPlanDrawer's old "Book Visit 1" flow grabbed the FIRST eligible
// item via Array.find() and silently booked only that procedure to the
// chosen appointment, leaving every other Accepted item unscheduled
// with no on-screen signal. For TP-5EBA83 (bridge + tooth 12 + tooth
// 16) the bridge got booked and the others were dropped — exactly the
// "silent drop" the receptionist saw.
//
// Behavior
// --------
// * Lists every Accepted-and-unscheduled item on the plan, grouped by
//   phase_number (Visit 1 / Visit 2 / …).
// * Smart default = the lowest-phase group is pre-checked. The
//   receptionist can check more (book multiple visits' worth of
//   procedures into one appointment) or uncheck some (defer one to a
//   later visit). Guided Freedom — guide with phase grouping, but
//   never silently exclude.
// * "Continue" returns the selected item IDs to the caller, which
//   then opens BookFutureNeedDialog with those items as the bundle.
//
// Single-item plans skip this dialog entirely (no decision to make).
// =====================================================================

export default function SelectVisitProceduresDialog({
  open,
  onOpenChange,
  items,           // Array of Accepted+unscheduled treatment_plan_items
  currency = "PHP",
  patientName,
  planCode,
  // On-the-go bundles have no fixed visit order or end, so the visit group
  // reads "Next visit" instead of a numbered "Visit N".
  planIsBundle = false,
  onConfirm,       // (selectedIds: string[]) => void
  // When provided, line-level prices are hidden and the footer shows
  // plan-level balance numbers instead. Patients are billed on the
  // plan's billing model (50/50, Even, Custom DP), not per-procedure-
  // per-visit, so per-line totals during booking are misleading. The
  // footer surfaces the relevant figure: how much of the plan is
  // still owed. Pass the get_plan_billing_state response shape:
  // { final_total_centavos, paid_centavos, remaining_centavos }.
  planBalance,
}) {
  // Group by phase_number for display + smart default.
  const phases = useMemo(() => {
    const map = new Map();
    for (const it of (items || [])) {
      const k = Number(it.phase_number || 1);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    }
    return Array.from(map.entries())
      .map(([phaseNum, rows]) => ({ phaseNum, rows }))
      .sort((a, b) => a.phaseNum - b.phaseNum);
  }, [items]);

  // Smart default: pre-check every item in the lowest unscheduled phase.
  // Empty plan → empty set.
  const defaultSelectedIds = useMemo(() => {
    if (phases.length === 0) return new Set();
    const first = phases[0];
    return new Set(first.rows.map((r) => r.id));
  }, [phases]);

  const [selected, setSelected] = useState(defaultSelectedIds);

  // Reset selection whenever the dialog (re-)opens or the eligible item
  // list changes.
  useEffect(() => {
    if (open) setSelected(new Set(defaultSelectedIds));
  }, [open, defaultSelectedIds]);

  if (!open) return null;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePhase = (phaseNum) => {
    const phaseRows = phases.find((p) => p.phaseNum === phaseNum)?.rows ?? [];
    const allChecked = phaseRows.every((r) => selected.has(r.id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) {
        for (const r of phaseRows) next.delete(r.id);
      } else {
        for (const r of phaseRows) next.add(r.id);
      }
      return next;
    });
  };

  const totalCount    = (items || []).length;
  const selectedCount = selected.size;
  const skippedCount  = totalCount - selectedCount;
  const selectedRows = (items || []).filter((it) => selected.has(it.id));
  const selectedTotal = selectedRows.reduce((acc, it) => acc + (it.fee_centavos || 0), 0);
  const selectedHasCaseFee = selectedRows.some((it) => it.fee_display?.isCaseFee);
  const selectedCaseTotal = selectedRows.reduce((acc, it) => {
    const key = it.item_id || it.treatment_plan_item_id || it.id;
    if (!it.fee_display?.isCaseFee) return acc;
    if (acc.seen.has(key)) return acc;
    acc.seen.add(key);
    acc.total += Number(it.fee_display.caseTotalCentavos || 0);
    return acc;
  }, { total: 0, seen: new Set() }).total;

  const close = () => onOpenChange(false);

  const onContinue = () => {
    if (selectedCount === 0) return;
    onConfirm?.(Array.from(selected));
  };

  return (
    <div
      role="dialog" aria-modal="true"
      data-testid="select-visit-procedures-dialog"
      data-tutorial-id="select-visit-procedures-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-teal-600" strokeWidth={2.25} />
              Pick procedures for this visit
            </h2>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              <span className="text-slate-700 font-medium">{patientName}</span>
              {planCode ? <> · <span className="font-mono">{planCode}</span></> : null}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            data-testid="select-visit-procedures-close"
            className="p-1 rounded hover:bg-slate-100 text-slate-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-3 overflow-y-auto flex-1">
          {phases.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">
              No procedures waiting to be booked.
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[12px] text-slate-600">
                Check every procedure that should go into the next appointment.
                Unchecked items stay on the plan and can be booked into a later visit.
              </p>

              {phases.map(({ phaseNum, rows }) => {
                const allChecked = rows.every((r) => selected.has(r.id));
                const someChecked = !allChecked && rows.some((r) => selected.has(r.id));
                return (
                  <section
                    key={phaseNum}
                    className="rounded-lg border border-slate-200 overflow-hidden"
                    data-testid={`select-visit-procedures-phase-${phaseNum}`}
                    data-tutorial-id={phaseNum === 2 ? "select-visit-procedures-phase-2" : `select-visit-procedures-phase-${phaseNum}`}
                  >
                    <header className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={allChecked}
                          ref={(el) => { if (el) el.indeterminate = someChecked; }}
                          onChange={() => togglePhase(phaseNum)}
                          aria-label={planIsBundle ? "Toggle next visit" : `Toggle Visit ${phaseNum}`}
                          data-testid={`select-visit-procedures-phase-toggle-${phaseNum}`}
                          className="w-4 h-4 accent-teal-600"
                        />
                        <span className="text-[12px] font-semibold text-slate-900">
                          {planIsBundle ? "Next visit" : `Visit ${phaseNum}`}
                        </span>
                        <span className="text-[11px] text-slate-500">
                          · {rows.length} procedure{rows.length === 1 ? "" : "s"}
                        </span>
                      </div>
                    </header>
                    <ul className="divide-y divide-slate-100">
                      {rows.map((it) => {
                        const checked = selected.has(it.id);
                        const feeDisplay = it.fee_display || null;
                        return (
                          <li key={it.id}>
                            <label
                              className="flex items-baseline gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 text-[12px]"
                              data-testid={`select-visit-procedures-item-${it.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggle(it.id)}
                                className="w-4 h-4 accent-teal-600 self-center"
                              />
                              <span className="flex-1 min-w-0 text-slate-900 truncate">
                                {it.display_label || it.procedure_name || "Procedure"}
                              </span>
                              {it.tooth_number ? (
                                <span className="text-[10px] text-slate-500 font-mono shrink-0">
                                  #{it.tooth_number}{it.surface ? ` · ${it.surface}` : ""}
                                </span>
                              ) : null}
                              {planBalance ? null : (
                                feeDisplay ? (
                                  <span className="shrink-0 w-28 text-right leading-tight">
                                    <span className={[
                                      "block font-semibold tabular-nums",
                                      feeDisplay.muted ? "text-slate-700" : "text-slate-900",
                                    ].join(" ")}
                                    >
                                      {feeDisplay.value}
                                    </span>
                                    <span className="block text-[10px] font-medium text-slate-500 whitespace-normal">
                                      {feeDisplay.label}
                                    </span>
                                  </span>
                                ) : (
                                  <span className="font-semibold tabular-nums text-slate-900 shrink-0 w-20 text-right">
                                    {formatMoney(it.fee_centavos, currency)}
                                  </span>
                                )
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-3 shrink-0">
          <div className="text-[11px] text-slate-600 flex-1 min-w-0">
            <div>
              <strong className="tabular-nums">{selectedCount}</strong>
              {" of "}
              <strong className="tabular-nums">{totalCount}</strong>
              {" selected"}
              {planBalance ? (
                <>
                  {" · Plan balance "}
                  <span className="font-semibold text-slate-900">
                    {formatMoney(planBalance.remaining_centavos ?? 0, currency)}
                  </span>
                  {planBalance.final_total_centavos > 0 ? (
                    <span className="text-slate-500">
                      {" of "}{formatMoney(planBalance.final_total_centavos, currency)}
                    </span>
                  ) : null}
                </>
              ) : (
                <>
                  {" · "}
                  {selectedHasCaseFee ? (
                    <>
                      {"Case total "}
                      <span className="font-semibold text-slate-900">
                        {formatMoney(selectedCaseTotal, currency)}
                      </span>
                    </>
                  ) : (
                    <span className="font-semibold text-slate-900">{formatMoney(selectedTotal, currency)}</span>
                  )}
                </>
              )}
            </div>
            {skippedCount > 0 ? (
              <div className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" strokeWidth={2.25} />
                {skippedCount} procedure{skippedCount === 1 ? "" : "s"} stay on the plan for a later visit.
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={close}
            data-testid="select-visit-procedures-cancel"
            className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onContinue}
            disabled={selectedCount === 0}
            data-testid="select-visit-procedures-continue"
            data-tutorial-id="select-visit-procedures-continue"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            <Calendar className="w-3.5 h-3.5" strokeWidth={2.25} />
            Continue · pick a slot
          </button>
        </div>
      </div>
    </div>
  );
}
